import { z } from 'zod';
import { financeCreateInvoiceDraftRequestSchema, financeInvoiceCommandIdSchema, financeInvoiceDetailSchema, financeInvoiceIdSchema, financeInvoiceLineSchema, financeMoneyCentsSchema, financeProjectReferenceIdSchema, financeTaxBasisPointsSchema, } from './finance-invoice.contract.js';
import { financeEstimatingBillableRevisionsCapability, } from './finance-estimating-billable-revision.contract.js';
export const financeInvoiceContentCapability = 'finance.invoice-content.v1';
export const financeInvoiceManualContentSourceSchema = z.strictObject({
    kind: z.literal('manual'),
});
export const financeInvoiceEstimatingContentSourceSchema = z.strictObject({
    kind: z.literal('estimating'),
    capability: z.literal(financeEstimatingBillableRevisionsCapability),
    estimateId: z.string().uuid(),
    estimateVersionId: z.string().uuid(),
    estimateRevision: z.number().int().positive(),
    estimateStatus: z.enum(['draft', 'released', 'viewed', 'accepted', 'declined', 'expired']),
    estimateContentHash: z.string().regex(/^[a-f0-9]{64}$/),
    projectId: financeProjectReferenceIdSchema,
    projectRevision: z.number().int().positive(),
    title: z.string().trim().min(1).max(240),
});
export const financeInvoiceContentSourceSchema = z.discriminatedUnion('kind', [
    financeInvoiceManualContentSourceSchema,
    financeInvoiceEstimatingContentSourceSchema,
]);
export const financeInvoiceContentVersionSchema = z.strictObject({
    id: z.string().uuid(),
    invoiceId: financeInvoiceIdSchema,
    contentRevision: z.number().int().positive(),
    invoiceRevision: z.number().int().positive(),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    source: financeInvoiceContentSourceSchema,
    currencyCode: z.literal('USD'),
    lineItems: z.array(financeInvoiceLineSchema).min(1).max(100),
    subtotalCents: financeMoneyCentsSchema,
    discountCents: financeMoneyCentsSchema,
    taxBasisPoints: financeTaxBasisPointsSchema,
    taxCents: financeMoneyCentsSchema,
    totalCents: financeMoneyCentsSchema,
    reason: z.string().trim().min(1).max(1_000),
    createdAt: z.string().datetime({ offset: true }),
}).superRefine((version, context) => {
    const subtotal = version.lineItems.reduce((sum, line) => sum + BigInt(line.lineTotalCents), 0n);
    if (subtotal !== BigInt(version.subtotalCents)) {
        context.addIssue({
            code: 'custom', path: ['subtotalCents'],
            message: 'Finance content-version lines must reconcile to their subtotal.',
        });
    }
    if (version.totalCents !== version.subtotalCents - version.discountCents + version.taxCents) {
        context.addIssue({
            code: 'custom', path: ['totalCents'],
            message: 'Finance content-version totals must reconcile exactly.',
        });
    }
});
export const financeCreateInvoiceFromEstimatingRevisionRequestSchema = z.strictObject({
    commandId: financeInvoiceCommandIdSchema,
    projectId: financeProjectReferenceIdSchema,
    estimateId: z.string().uuid().nullable().default(null),
    expectedEstimateRevision: z.number().int().positive(),
    expectedEstimateContentHash: z.string().regex(/^[a-f0-9]{64}$/),
    invoiceDate: financeCreateInvoiceDraftRequestSchema.shape.invoiceDate,
    dueDate: financeCreateInvoiceDraftRequestSchema.shape.dueDate,
    memo: z.string().trim().max(2_000).nullable().default(null),
});
const contentRevisionBaseSchema = z.strictObject({
    commandId: financeInvoiceCommandIdSchema,
    expectedInvoiceRevision: z.number().int().positive(),
    reason: z.string().trim().min(1).max(1_000),
});
export const financeReviseInvoiceManualContentCommandSchema = contentRevisionBaseSchema.extend({
    sourceKind: z.literal('manual'),
    discountCents: financeMoneyCentsSchema,
    taxBasisPoints: financeTaxBasisPointsSchema,
    items: financeCreateInvoiceDraftRequestSchema.shape.items,
});
export const financeReviseInvoiceFromEstimatingCommandSchema = contentRevisionBaseSchema.extend({
    sourceKind: z.literal('estimating'),
    estimateId: z.string().uuid(),
    expectedEstimateRevision: z.number().int().positive(),
    expectedEstimateContentHash: z.string().regex(/^[a-f0-9]{64}$/),
});
export const financeReviseInvoiceContentCommandSchema = z.discriminatedUnion('sourceKind', [
    financeReviseInvoiceManualContentCommandSchema,
    financeReviseInvoiceFromEstimatingCommandSchema,
]);
export const financeInvoiceContentResultSchema = z.strictObject({
    data: z.strictObject({
        invoice: financeInvoiceDetailSchema,
        version: financeInvoiceContentVersionSchema,
    }),
    idempotent: z.boolean(),
});
export const financeInvoiceContentVersionsResponseSchema = z.strictObject({
    data: z.array(financeInvoiceContentVersionSchema).min(1).max(1_000),
});
//# sourceMappingURL=finance-invoice-content.contract.js.map